// Exposure atlas: open-water distance up-wave ("fetch") for 16 directions on a 100 m grid (tools/exposure.py).
// Turns the offshore swell / wind-sea numbers into what actually arrives at each patch of water.
import META from './data/exposure.json';

export class Exposure {
  constructor() { this.bands = null; this.ready = this.load(); }
  async load() {
    const img = new Image(); img.src = '/data/exposure.png';
    await img.decode();
    const c = document.createElement('canvas'); c.width = img.width; c.height = img.height;
    const g = c.getContext('2d', { willReadFrequently: true }); g.drawImage(img, 0, 0);
    const d = g.getImageData(0, 0, img.width, img.height).data;
    const n = META.w * META.h; this.bands = [];
    for (let k = 0; k < META.dirs; k++) { const b = new Uint8Array(n); for (let i = 0; i < n; i++) b[i] = d[((k * META.h * META.w) + i) * 4]; this.bands.push(b); }
    this.mlon = 111320 * Math.cos(18.5 * Math.PI / 180); this.mlat = 110540;
  }
  // fetch in km looking toward `dirFrom` (compass, degrees) from lon/lat; null outside the atlas; 0 on land
  fetchKm(lon, lat, dirFrom) {
    if (!this.bands) return null;
    const fx = (lon - META.lon0) * this.mlon / 100, fy = (META.lat1 - lat) * this.mlat / 100;
    if (fx < 0 || fy < 0 || fx >= META.w - 1 || fy >= META.h - 1) return null;
    const x0 = fx | 0, y0 = fy | 0, tx = fx - x0, ty = fy - y0;
    const a = ((dirFrom % 360) + 360) % 360 / 22.5; const k0 = Math.floor(a) % 16, k1 = (k0 + 1) % 16, tk = a - Math.floor(a);
    const samp = (b) => {
      const i = y0 * META.w + x0;
      return ((b[i] * (1 - tx) + b[i + 1] * tx) * (1 - ty) + (b[i + META.w] * (1 - tx) + b[i + META.w + 1] * tx) * ty) / 10;
    };
    return samp(this.bands[k0]) * (1 - tk) + samp(this.bands[k1]) * tk;
  }
  // directional spread: weight rays around the mean direction, so shadows have soft edges like real swell
  spreadFetch(lon, lat, dirFrom, spread = 30) {
    const ws = [[-spread, 0.12], [-spread / 2, 0.22], [0, 0.32], [spread / 2, 0.22], [spread, 0.12]];
    let s = 0, w = 0;
    for (const [d, k] of ws) { const f = this.fetchKm(lon, lat, dirFrom + d); if (f == null) continue; s += f * k; w += k; }
    return w ? s / w : null;
  }
  // how much of the offshore swell survives here (0..1): needs ~20 km of open water to be fully exposed;
  // in a tight lee about a fifth still wraps in
  swellFactor(lon, lat, dirFrom) { const f = this.spreadFetch(lon, lat, dirFrom, 30); if (f == null) return 1; return 0.2 + 0.8 * Math.min(1, f / 20); }
  // wind sea regrows quickly with fetch: ~8 km of open water rebuilds most of it
  windSeaFactor(lon, lat, dirFrom) { const f = this.spreadFetch(lon, lat, dirFrom, 20); if (f == null) return 1; return 0.3 + 0.7 * Math.min(1, f / 8); }
  // effective sea state at a point from the model's offshore numbers
  seaAt(field, lon, lat, t) {
    const c = field.at(lon, lat, t);
    const fs = this.swellFactor(lon, lat, c.sd), fw = this.windSeaFactor(lon, lat, c.wd);
    const windSea = Math.sqrt(Math.max(0, c.wh * c.wh - c.sh * c.sh));
    const sh = c.sh * fs, ws = windSea * fw;
    const r2 = x => Math.round(x * 100) / 100;
    return { ...c, sh0: c.sh, wh0: c.wh, sh: r2(sh), wsea: r2(ws), wh: r2(Math.sqrt(sh * sh + ws * ws)), fs, fw };
  }
}
